
// import './assets/Table.css'
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { 
    setSelectedVehicle,
} from './redux/vehicleSlice.js'
import { useNavigate } from 'react-router-dom';

import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import apiClient from './services/apiClient.js';


const VehicleListV2 = ({client}) => {

    const dispatch = useDispatch()
    const navigate = useNavigate();
    const isAutoScan = useSelector((state) => state.autoScan.isAutoScan);
    const vehicleListDivRef = React.useRef(null);
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const [isLoadingVehicles, _setIsLoadingVehicles] = useState(true);
    
    const isLoadingVehiclesRef = React.useRef(isLoadingVehicles);

    const [vehicles, _setVehicles] = useState([]);
    const vehiclesStateRef = React.useRef([]);
    const setVehicles = data => {
            vehiclesStateRef.current = data;
            _setVehicles(data);
    };

    // Alarm refs for continuous drive-off siren
    const alarmAudioContextRef = React.useRef(null);
    const alarmOscillatorRef = React.useRef(null);
    const alarmIntervalRef = React.useRef(null);

    // const [vehicleHistoryCounts, setVehicleHistoryCounts] = useState({});

    // Audio alert for all vehicles
    const playVehicleDetectionAlert = async () => {
        console.log('🔔 playVehicleDetectionAlert() called');
        
        // Check if sound is enabled in settings
        const soundEnabled = localStorage.getItem('allVehicleSoundEnabled');
        console.log('🔔 allVehicleSoundEnabled from localStorage:', soundEnabled);
        
        if (soundEnabled !== null && !JSON.parse(soundEnabled)) {
            console.log('🔇 All vehicle alert sound is disabled in settings');
            return; // Silently return if disabled
        }
        
        console.log('🔊 Playing all vehicle detection beep...');
        
        try {
            // Create an audio context and play a short, gentle beep
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Resume audio context if suspended (required for some browsers)
            if (audioContext.state === 'suspended') {
                console.log('🔊 AudioContext suspended, attempting to resume...');
                await audioContext.resume();
            }
            
            console.log('🔊 AudioContext state:', audioContext.state);
            
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.value = 800; // Gentle mid-tone
            oscillator.type = 'sine';
            
            gainNode.gain.setValueAtTime(0.2, audioContext.currentTime); // Quieter than drive-off alert
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.1); // Very short beep (100ms)
            
            console.log('✅ All vehicle detection beep played successfully');
        } catch (error) {
            console.error('❌ Error playing all vehicle detection beep:', error);
        }
    };

    // Continuous looping siren for drive-off detection
    const startDriveOffAlarm = async () => {
        // Check if sound is enabled in settings
        const soundEnabled = localStorage.getItem('driveOffSoundEnabled');
        if (soundEnabled !== null && !JSON.parse(soundEnabled)) {
            console.log('🔇 Drive-off alert sound is disabled in settings');
            return;
        }

        // Don't stack alarms - if already playing, do nothing
        if (alarmIntervalRef.current) {
            console.log('🔊 Alarm already playing, not stacking');
            return;
        }

        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            if (audioContext.state === 'suspended') {
                console.log('🔊 AudioContext suspended, attempting to resume...');
                await audioContext.resume();
            }
            alarmAudioContextRef.current = audioContext;

            // Create a looping siren: alternates between 800Hz and 1200Hz every 0.5s
            let isHigh = false;
            const playSirenCycle = () => {
                // Stop previous oscillator if exists
                if (alarmOscillatorRef.current) {
                    try { alarmOscillatorRef.current.stop(); } catch(e) {}
                }

                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                oscillator.frequency.value = isHigh ? 1200 : 800;
                oscillator.type = 'sawtooth'; // Harsher than sine - more alarm-like
                gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);

                oscillator.start(audioContext.currentTime);
                // Each tone lasts 0.5s, then the interval callback creates the next one
                oscillator.stop(audioContext.currentTime + 0.5);

                alarmOscillatorRef.current = oscillator;
                isHigh = !isHigh;
            };

            // Play first cycle immediately, then repeat every 500ms
            playSirenCycle();
            alarmIntervalRef.current = setInterval(playSirenCycle, 500);

            console.log('🚨 Drive-off siren alarm started (continuous loop)');
        } catch (error) {
            console.error('❌ Error starting drive-off alarm:', error);
        }
    };

    const stopDriveOffAlarm = () => {
        if (alarmIntervalRef.current) {
            clearInterval(alarmIntervalRef.current);
            alarmIntervalRef.current = null;
            console.log('🛑 Drive-off alarm stopped');
        }
        if (alarmOscillatorRef.current) {
            try { alarmOscillatorRef.current.stop(); } catch(e) {}
            alarmOscillatorRef.current = null;
        }
        if (alarmAudioContextRef.current) {
            alarmAudioContextRef.current.close();
            alarmAudioContextRef.current = null;
        }
    };

    const setIsLoadingVehicles = data => {
        isLoadingVehiclesRef.current = data;
        _setIsLoadingVehicles(data);
    };

    const handleOnVehicleSelected = (vehicle, initialView = 'report') => {
        stopDriveOffAlarm(); // Stop the siren when any tile is clicked
        dispatch(setSelectedVehicle(vehicle));
        const normalizedPlate = (vehicle.plate || 'unknown').replace(/-/g, '').toUpperCase();
        const query = new URLSearchParams();
        if (vehicle.dateTimeUtc) query.set('dateTimeUtc', vehicle.dateTimeUtc);
        query.set('view', initialView);

        navigate(`/vehicle/${normalizedPlate}${query.toString() ? `?${query.toString()}` : ''}`, {
            state: { vehicle, initialView },
        });
    };

const loadVehiclesForGroupAsync = async (pageNumber) => {

    if (!isAuthenticated || user['/roles'].length === 0) {
        return;
     }

        const accessToken = await getAccessTokenSilently({
            authorizationParams: {
                  audience: process.env.REACT_APP_AUTH0_API_AUDIENCE,
              }
          });
          const userRole = user['/roles'][0];
        const batchSize = process.env.REACT_APP_VEHICLE_LIST_BATCH_SIZE;
        const params = {
            groupName: userRole,
            pageNumber,
            batchSize
        };

        const result = await apiClient.getVehiclesForGroupAsync(params, accessToken)
        if(pageNumber === 1)
        {
            // console.log(`📋 Loaded ${result.length} vehicles on initial fetch`);
            
            // Debug: Log all vehicles with their isDriveOff status
            // result.forEach((vehicle, index) => {
            //     console.log(`Vehicle ${index + 1}: ${vehicle.plate} - isDriveOff: ${vehicle.isDriveOff}`);
            // });
            
            // Check if any of the loaded vehicles have unpaid drive-offs
            const driveOffVehicles = result.filter(vehicle => vehicle.isActiveDriveOff);
            // console.log(`🔍 Found ${driveOffVehicles.length} vehicles with isDriveOff === true`);
            
            if (driveOffVehicles.length > 0) {
                // console.log(`🚨 ALERT: ${driveOffVehicles.length} vehicle(s) with unpaid drive-offs detected!`);
                // driveOffVehicles.forEach(vehicle => {
                //     console.log(`   - ${vehicle.plate} (detected at ${vehicle.dateTimeUtc})`);
                // });
                // Start continuous alarm sound
                // console.log('🔊 Starting drive-off alarm siren...');
                startDriveOffAlarm();
            } else {
                // console.log('✅ No drive-off vehicles found in initial load');
            }
            
            setVehicles(result)
            setIsLoadingVehicles(false);
            // console.log(`Initial Fetch done - ${componentAlertStatus}`);
            return;
        }
        const vehiclesState = vehiclesStateRef.current;
        //Add to the end as this is paging sorted by date descending. 
        setVehicles([...vehiclesState, ...result]);
        setIsLoadingVehicles(false);
}

const isAtBottomOfPage = (threshold = 100) => {
    return window.innerHeight + window.scrollY >= document.body.offsetHeight - threshold;
}

const trackScrolling = () => {
    if (!isAtBottomOfPage(100)) {
        return;
    }
    const batchSize = process.env.REACT_APP_VEHICLE_LIST_BATCH_SIZE;
    const vehiclesState = vehiclesStateRef.current;
    if (vehiclesState.length === 0) return;
    const isLoadingVehiclesState = isLoadingVehiclesRef.current;
    // Only load if not already loading and last batch was full
    if (!isLoadingVehiclesState && vehiclesState.length % batchSize === 0) {
        setIsLoadingVehicles(true);
        const vehiclesListPageNo = vehiclesState.length / batchSize;
        loadVehiclesForGroupAsync(vehiclesListPageNo + 1);
    }
}

useEffect(() => {
    // Add scroll listener to window for infinite scroll
    window.addEventListener('scroll', trackScrolling, { passive: true });
    // Load first page of the list
    loadVehiclesForGroupAsync(1);
    // Subscribed to pubsub group messages
    client.on("group-message", (e) => {
        const message = e.message.data;
        const alertStatus = message.alertStatus;
        // console.log(alertStatus)
    
        // if(componentAlertStatus === alertStatus) {
          const vehiclesState = vehiclesStateRef.current;
          
          // Check for duplicates before adding new vehicle
          const isDuplicate = vehiclesState.some(existingVehicle => 
            existingVehicle.plate === message.plate && 
            existingVehicle.dateTimeUtc === message.dateTimeUtc
          );
          
          if (!isDuplicate) {
            // console.log(`🚗 New vehicle detected: ${message.plate}, isDriveOff: ${message.isDriveOff}`);
            
            // Play audio alerts
            if (message.isActiveDriveOff) {
              // Urgent drive-off alert - start continuous siren
              // console.log(`🚨 DRIVE-OFF ALERT: Vehicle ${message.plate} detected!`);
              startDriveOffAlarm();
            } else {
              // Gentle beep for all vehicles (if enabled)
              // console.log(`🔔 Regular vehicle detected, calling playVehicleDetectionAlert()...`);
              playVehicleDetectionAlert();
            }
            
          setVehicles([...[e.message.data], ...vehiclesState]);
          } else {
            // console.log(`Duplicate vehicle detection ignored: ${message.plate} at ${message.dateTimeUtc}`);
          }
        //   return;
        // }

        // ...existing code...
    });
    return () => {
        window.removeEventListener("scroll", trackScrolling);
        stopDriveOffAlarm(); // Clean up alarm on unmount
        vehiclesStateRef.current?.close && vehiclesStateRef.current.close();
        vehiclesStateRef.current = null;
    };
}, []);

const getTimeOnly = (vehicle) => {
    const locale = (navigator && navigator.language) || "en"
    const timeOnly = vehicle.dateTimeUtc ? new Date(vehicle.dateTimeUtc).toLocaleString(locale, {
        timeStyle: "short"
    }) : null 
    return timeOnly;
}

const formatPlate = (plate) => {
    if (!plate || plate === 'unknown') return plate;
    // Add dash after 3rd character if plate is exactly 6 characters
    if (plate.length === 6) {
        return `${plate.substring(0, 3)}-${plate.substring(3)}`;
    }
    return plate;
}

const getVehicleImageUrl = (vehicle) => {
    if (vehicle.vehicleImageUrl && vehicle.vehicleImageUrl.length !== 0)
        return vehicle.vehicleImageUrl;

    if (vehicle.regoPlateImageUrl && vehicle.regoPlateImageUrl.length !== 0)
        return vehicle.regoPlateImageUrl;
}

const vehicleCards = vehicles.map((vehicle, index) => {
    const isVehicleNotFound = isAutoScan && vehicle.message === "NOT FOUND";
    const vehicleImageUrl = getVehicleImageUrl(vehicle);
    const historyCount = vehicle.historyCount;

    const alertStatusClassNames = classNames({
        'red-alert-status': vehicle.alertStatus === 'Red',
        'green-alert-status': vehicle.alertStatus === 'Green',
        'amber-alert-status': vehicle.alertStatus === 'Amber',
        'neutral-alert-status': vehicle.alertStatus === 'Neutral'
    })
    

    return (
        <div key={`vehicle-${vehicle.plate}-${index}`} className="col-12 col-md-6 col-lg-4 col-xl-3">
            <div 
                className={`card h-100 ${vehicle.isActiveDriveOff ? 'drive-off-alert' : ''}`}
                style={{
                    borderRadius: '16px',
                    borderWidth: vehicle.isActiveDriveOff ? '3px' : '1px',
                    borderColor: vehicle.isActiveDriveOff ? '#dc3545' : '#e9ecef',
                    boxShadow: vehicle.isActiveDriveOff ? '0 4px 20px rgba(220, 53, 69, 0.3)' : '0 2px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease', 
                    cursor: 'pointer',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '300px',
                }}
                onClick={() => handleOnVehicleSelected(vehicle, 'report')}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = vehicle.isActiveDriveOff ? '0 8px 30px rgba(220, 53, 69, 0.4)' : '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = vehicle.isActiveDriveOff ? '0 4px 20px rgba(220, 53, 69, 0.3)' : '0 2px 12px rgba(0,0,0,0.08)';
                }}
            >
                {/* Vehicle Image */}
                <div 
                    className="position-relative" 
                    style={{
                        position: 'relative',
                        flex: '1 1 auto',
                        overflow: 'hidden',
                        backgroundColor: '#0B122A',
                    }}
                >
                    <img 
                        src={vehicleImageUrl} 
                        alt={vehicle.plate}
                        className="card-img-top w-100"
                        style={{
                            height: '125%',
                            marginTop: '0%',
                            objectFit: 'cover',
                            objectPosition: 'center center',
                        }}
                    />
                    
                    {/* Status Badge */}
                    {(vehicle.isVehicleStolen || isVehicleNotFound) && (
                        <div className="position-absolute top-0 start-0 m-2">
                            <span className="badge px-3 py-2" style={{
                                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                fontSize: '12px',
                                fontWeight: '600',
                                borderRadius: '50px',
                                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                <i className="fa fa-shield-exclamation"></i>
                                {vehicle.isVehicleStolen ? 'STOLEN' : 'NOT FOUND'}
                            </span>
                        </div>
                    )}
                    
                    {/* Plate Overlay (image when available, text fallback) */}
                    <div
                        style={{
                            position: 'absolute',
                            left: '5%',
                            bottom: '5%',
                            width: '96px',
                            height: '42px',
                            zIndex: 4,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '2px solid #ffffff',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
                            backgroundColor: 'rgba(11, 18, 42, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: vehicle.regoPlateImageUrl ? 0 : '0 6px',
                        }}
                    >
                        {vehicle.regoPlateImageUrl && vehicle.regoPlateImageUrl.length !== 0 ? (
                            <img
                                src={vehicle.regoPlateImageUrl}
                                alt={`Plate ${vehicle.plate}`}
                                className="img-fluid h-100 w-100"
                                style={{ objectFit: 'cover' }}
                            />
                        ) : (
                            <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>
                                {formatPlate(vehicle.plate || 'UNKNOWN')}
                            </span>
                        )}
                    </div>
                    
                    {/* Drive-Off Alert Badge */}
                    {vehicle.isActiveDriveOff && (
                        <div className="position-absolute bottom-0 start-0 end-0 m-0 px-3 py-2" 
                             style={{
                                 background: 'linear-gradient(to top, rgba(220,38,38,0.98), rgba(220,38,38,0.85))',
                                 backdropFilter: 'blur(8px)'
                             }}>
                            <div className="d-flex align-items-center justify-content-center gap-2 text-white fw-bold" style={{fontSize: '13px'}}>
                                <i className="fa fa-shield-exclamation"></i>
                                UNPAID DRIVE-OFF
                            </div>
                        </div>
                    )}

                </div>
                
                {/* Card Footer: stats strip */}
                <div 
                    className={`card-body p-3 ${alertStatusClassNames}`} 
                    style={{
                        minHeight: '43px',
                        maxHeight: '43px',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <div className="d-flex flex-column w-100 h-100 justify-content-center">
                        {vehicle.plate !== 'unknown' ? (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    alignItems: 'center',
                                    width: '100%',
                                    minHeight: '34px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        paddingRight: '8px',
                                        borderRight: '1px solid rgba(255,255,255,0.25)',
                                        minHeight: '34px',
                                    }}
                                >
                                    <span style={{ fontSize: '19px', fontWeight: '700', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>
                                        {formatPlate(vehicle.plate)}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: historyCount > 0 ? 1 : 0.7,
                                        paddingLeft: '8px',
                                        minHeight: '34px',
                                    }}
                                >
                                    <span style={{ fontSize: '19px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                        {historyCount} visit{historyCount !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center w-100">
                                <span style={{ fontSize: '16px', fontWeight: '500' }}>
                                    Unknown Plate
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});


const loadingSpinner = isLoadingVehicles ? 
(
    <div className="d-flex justify-content-center align-items-center py-4">
        <div className="spinner-border text-primary" role="status" style={{color: '#1E70E0'}}>
            <span className="visually-hidden">Loading</span>
        </div>
    </div>
): null;

   return (
        <div className="row g-3" ref={vehicleListDivRef}>
                    {vehicleCards}
                    {loadingSpinner}
                </div>
    );
}

export default VehicleListV2;
 